import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { Icon } from '..';
import { IconMap } from '../../sprite';
import { cn } from '@distribute/frontend/utils';

type Props<T extends { id: number | string }> = {
  errors?: boolean;
  selected?: T | undefined;
  setSelected: (item: T) => void;
  renderIcon?: (item: T) => React.ReactNode;
  renderItem: (item: T) => React.ReactNode;
  renderName: (item: T) => string;
  filterBy: (search: string, item: T) => boolean;

  items: T[];
  placeholder?: string;
  id?: string;
};

export const ComboBoxSingle = <T extends { id: number | string }>({
  errors,
  selected,
  setSelected,
  renderItem,
  renderIcon,
  renderName,
  filterBy,
  items,
  placeholder = 'Start typing to select ...',
  id,
}: Props<T>) => {
  const ref = useRef<HTMLButtonElement>(null);
  const refInput = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  const handleFocus = () => {
    setTimeout(() => {
      // Focus input at the end of the value
      const _value = refInput.current?.value;
      if (refInput.current) {
        refInput.current.value = '';
        refInput.current.focus();
        refInput.current.value = _value || '';
      }
    }, 0);
  };

  useEffect(() => {
    if (!refInput.current) {
      return;
    }

    // Reset input value when selected is undefined
    if (selected === undefined) {
      refInput.current.value = '';
    }
  }, [selected]);

  const filteredItems = useMemo(
    () =>
      query === '' ? items : items.filter((item) => filterBy(query, item)),
    [query, items, filterBy]
  );

  return (
    <Combobox defaultValue={selected} onChange={(item) => setSelected(item)}>
      {({ open }) => (
        <div className="relative">
          <div className="h-11 relative w-full cursor-default overflow-hidden rounded-lg bg-base-white text-left shadow-xs focus:outline-none flex flex-nowrap items-center">
            <div className="pointer-events-none absolute left-3.5 top-0 bottom-0 flex items-center justify-center">
              {!open && selected && renderIcon?.(selected)}
            </div>
            <Combobox.Input
              id={id}
              placeholder={placeholder}
              ref={refInput}
              onClick={() => ref.current?.click()}
              autoComplete="off"
              className={cn(
                'flex-grow-1 border rounded-lg w-full h-full text-md text-gray-900 border-gray-300',
                'focus:ring-0 focus:border-primary-600 focus:outline-none',
                'disabled:bg-gray-50 disabled:text-gray-500',
                'placeholder-gray-500 text-ellipsis px-3.5',
                errors && 'border-error-600',
                !open && selected && !!renderIcon && 'pl-10.5'
              )}
              displayValue={(item: T | undefined) =>
                item ? renderName(item) : ''
              }
              onChange={(event) => setQuery(event.target.value)}
            />
            <Combobox.Button ref={ref} className="w-0 h-0" />
          </div>
          <Transition as={Fragment} afterLeave={() => setQuery('')}>
            <Combobox.Options className="mt-1 absolute w-full z-50 max-h-50 overflow-y-auto rounded-lg py-1 px-1.5 border border-gray-200 bg-base-white shadow-lg flex flex-col gap-y-0.5">
              {filteredItems.length === 0 ? (
                <div className="flex-shrink-0 h-11 truncate flex justify-between items-center relative cursor-default select-none px-2.5 text-md text-gray-600">
                  Nothing found.
                </div>
              ) : (
                filteredItems.map((item) => (
                  <Combobox.Option
                    onClick={handleFocus}
                    key={item.id}
                    className={({ active }) =>
                      cn(
                        `flex-shrink-0 truncate relative flex justify-between items-center gap-x-2 w-full cursor-pointer select-none rounded-md focus:bg-gray-50 hover:bg-gray-50 h-11 px-2.5 text-md font-medium text-gray-700 outline-none`,
                        active && 'bg-gray-50'
                      )
                    }
                    value={item}
                  >
                    {({ selected }) => (
                      <>
                        {renderItem(item)}
                        {selected && (
                          <Icon
                            glyph={IconMap.GreenCheckMark}
                            className="flex-shrink-0"
                          />
                        )}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  );
};
